import { useMappedState } from "redux-react-hook";
import * as React from "react";
import moment from "moment-timezone";
import _ from "lodash";

import { IStore } from "src/core/shared/store";
import {
  CustomerBranchFilterDefault,
  CustomerBranchFilterType
} from "src/core/products/api/products.api";
import useTimezone from "src/core/shared/hooks/time-helpers/useTimeZone";
import { useDrop } from "src/core/drops/hooks";
import { IDrop } from "src/core/drops/api/drops.api";

/** For a date provide a helper to return the lead time override for the current drops */
export const useLeadTimeOverrideCutoff = (inputDrop?: IDrop | null) => {
  const timezone = useTimezone();
  const [ { drop } ] = useDrop();

  const dropToUse = React.useMemo(() => {
    if (inputDrop) {
      return inputDrop;
    }

    return drop;
  }, [ drop, inputDrop ]);

  const leadTimeOverrides = useMappedState((store: IStore) => store.global.settings?.leadTimeOverrides);

  const leadTimeOverrideForDate = React.useCallback((orderDeliveryDate: Date) => {
    // Only do delivery date validation if it's a date-led order
    let cutoffOverride;
    const matchingDateLeadTimeOverrides = _.filter(leadTimeOverrides, lto => moment.utc(lto.deliveryDate).isSame(moment.utc(orderDeliveryDate), "day"));
    const allowNoneOverrides = _.filter(matchingDateLeadTimeOverrides, lto => lto.type === CustomerBranchFilterDefault.AllowNone);

    // loop through the allow none lead time types first as these would take priority
    for (const hasMatchingLeadTimeOverride of allowNoneOverrides) {
      const matchingBranchFilter = _.find(hasMatchingLeadTimeOverride.filters, ltoFilter => ltoFilter.branchId === dropToUse?.id);
      // match the branch first and fall back to a customer level filter
      const matchingFilter = matchingBranchFilter || _.find(hasMatchingLeadTimeOverride.filters, ltoFilter => ltoFilter.customerId === dropToUse?.customerId);

      // matching filter is an inclusion so allow it
      if (matchingFilter?.filterType === CustomerBranchFilterType.Include) {
        cutoffOverride = moment.tz(hasMatchingLeadTimeOverride.cutoffDateTime, timezone).toDate();
        break;
      }
    }

    // if no cutoff already
    if (!cutoffOverride) {
      const allowAllOverrides = _.filter(matchingDateLeadTimeOverrides, lto => lto.type === CustomerBranchFilterDefault.AllowAll);

      // then loop through the allow all if there was now match above
      for (const hasMatchingLeadTimeOverride of allowAllOverrides) {
        const matchingBranchFilter = _.find(hasMatchingLeadTimeOverride.filters, ltoFilter => ltoFilter.branchId === dropToUse?.id);
        // match the branch first and fall back to a customer level filter
        const matchingFilter = matchingBranchFilter || _.find(hasMatchingLeadTimeOverride.filters, ltoFilter => ltoFilter.customerId === dropToUse?.customerId);

        // when no filters and its an allow all
        if (!matchingFilter || matchingFilter.filterType !== CustomerBranchFilterType.Exclude) {
          cutoffOverride = moment.tz(hasMatchingLeadTimeOverride.cutoffDateTime, timezone).toDate();
          break;
        }
      }
    }

    return cutoffOverride;
  }, [
    dropToUse?.customerId,
    dropToUse?.id,
    leadTimeOverrides,
    timezone
  ]);

  return leadTimeOverrideForDate;
};
